import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { isPdfUrl, paymentProofHref } from '../../utils/fileUrl';
import {
  Wallet,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  X,
  Search
} from 'lucide-react';

const WalletManagement = () => {
  const [activeTab, setActiveTab] = useState('wallets'); // wallets, requests, history
  const [agents, setAgents] = useState([]);
  const [topupRequests, setTopupRequests] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showManualTopupModal, setShowManualTopupModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPaymentProofModal, setShowPaymentProofModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  
  const [manualTopupData, setManualTopupData] = useState({
    agent_id: '',
    amount: '',
    notes: ''
  });
  
  const [adminNotes, setAdminNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const openPaymentProof = async (storedUrl) => {
    const href = paymentProofHref(storedUrl);
    if (!href) return;
    try {
      const res = await axios.get(href, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      setPaymentProofUrl(blobUrl);
      setShowPaymentProofModal(true);
    } catch (err) {
      console.error('Failed to load payment proof:', err);
      alert('Could not load payment proof');
    }
  };

  const closePaymentProofModal = () => {
    if (paymentProofUrl.startsWith('blob:')) {
      URL.revokeObjectURL(paymentProofUrl);
    }
    setPaymentProofUrl('');
    setShowPaymentProofModal(false);
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      // Using cookie-based auth with withCredentials

      if (activeTab === 'wallets') {
        const response = await axios.get(`${API_URL}/api/admin/wallet/agents`, { withCredentials: true });
        setAgents(response.data.agents);
      } else if (activeTab === 'requests') {
        const response = await axios.get(`${API_URL}/api/admin/wallet/requests?status=pending`, { withCredentials: true });
        setTopupRequests(response.data.requests);
      } else {
        // Fetch all transactions
        const agentsRes = await axios.get(`${API_URL}/api/admin/wallet/agents`, { withCredentials: true });
        const transactions = [];
        
        for (const agent of agentsRes.data.agents) {
          const txnRes = await axios.get(
            `${API_URL}/api/admin/wallet/requests?status=approved`,
            { withCredentials: true }
          );
          transactions.push(...txnRes.data.requests);
        }
        
        setAllTransactions(transactions);
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualTopup = async (e) => {
    e.preventDefault();
    try {
      // Using cookie-based auth with withCredentials
      
      // Backend expects query parameters, not JSON body
      const params = new URLSearchParams({
        agent_id: manualTopupData.agent_id,
        amount: manualTopupData.amount,
        notes: manualTopupData.notes || ''
      });
      
      await axios.post(`${API_URL}/api/admin/wallet/topup?${params}`, null, { withCredentials: true });
      
      setShowManualTopupModal(false);
      setManualTopupData({ agent_id: '', amount: '', notes: '' });
      fetchData();
      alert('Wallet topped up successfully!');
    } catch (error) {
      console.error('Error processing topup:', error);
      alert(`Failed to process topup: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleApproveRequest = async () => {
    try {
      // Using cookie-based auth with withCredentials
      await axios.put(
        `${API_URL}/api/admin/wallet/requests/${selectedRequest.transaction_id}/approve`,
        { admin_notes: adminNotes },
        { withCredentials: true }
      );
      
      setShowApproveModal(false);
      setAdminNotes('');
      setSelectedRequest(null);
      fetchData();
      alert('Request approved successfully!');
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request');
    }
  };

  const handleRejectRequest = async () => {
    try {
      // Using cookie-based auth with withCredentials
      await axios.put(
        `${API_URL}/api/admin/wallet/requests/${selectedRequest.transaction_id}/reject`,
        { admin_notes: adminNotes },
        { withCredentials: true }
      );
      
      setShowRejectModal(false);
      setAdminNotes('');
      setSelectedRequest(null);
      fetchData();
      alert('Request rejected');
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Failed to reject request');
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.agency_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingCount = topupRequests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Wallet Management</h1>
        <p className="text-gray-600 mt-1">Manage agent wallets and topup requests</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('wallets')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'wallets'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Wallet size={18} />
              Agent Wallets
            </div>
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors relative ${
              activeTab === 'requests'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Clock size={18} />
              Topup Requests
              {pendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <DollarSign size={18} />
              Transaction History
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'wallets' && (
        <div className="space-y-4">
          {/* Search & Actions */}
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search agents..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => {
                setSelectedAgent(null);
                setShowManualTopupModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
            >
              <DollarSign size={20} />
              Manual Topup
            </button>
          </div>

          {/* Wallets Table */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Agency Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Agent Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Wallet Balance</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgents.map((agent, index) => (
                  <tr key={index} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-800">{agent.agency_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-800">{agent.full_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{agent.email}</td>
                    <td className="py-3 px-4 text-sm font-semibold text-right text-gray-800">
                      ₹{agent.wallet_balance?.toLocaleString() || 0}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        agent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {agent.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => {
                          setSelectedAgent(agent);
                          setManualTopupData({ ...manualTopupData, agent_id: agent.id });
                          setShowManualTopupModal(true);
                        }}
                        className="text-blue-600 hover:underline text-sm font-medium"
                      >
                        Topup
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Agent</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Agency</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Payment Proof</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {topupRequests.length > 0 ? (
                topupRequests.map((request, index) => (
                  <tr key={index} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-800">{request.agent_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{request.agency_name}</td>
                    <td className="py-3 px-4 text-sm font-semibold text-right text-gray-800">
                      ₹{request.amount?.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(request.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {request.payment_proof_url ? (
                        <button
                          onClick={() => openPaymentProof(request.payment_proof_url)}
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1 mx-auto"
                        >
                          <Eye size={16} />
                          View
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">No proof</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        request.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        request.status === 'approved' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {request.status === 'pending' && (
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowApproveModal(true);
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Approve"
                          >
                            <CheckCircle size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowRejectModal(true);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Reject"
                          >
                            <XCircle size={18} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-500">
                    No pending requests
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Transaction ID</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Agent</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {allTransactions.map((txn, index) => (
                <tr key={index} className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-800">{txn.transaction_id}</td>
                  <td className="py-3 px-4 text-sm text-gray-800">{txn.agent_name}</td>
                  <td className="py-3 px-4 text-sm font-semibold text-right text-green-600">
                    +₹{txn.amount?.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {new Date(txn.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      {txn.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual Topup Modal */}
      {showManualTopupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Manual Wallet Topup</h2>
              <button onClick={() => setShowManualTopupModal(false)}>
                <X className="text-gray-500 hover:text-gray-700" size={24} />
              </button>
            </div>

            <form onSubmit={handleManualTopup} className="space-y-4">
              {!selectedAgent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Agent *</label>
                  <select
                    required
                    value={manualTopupData.agent_id}
                    onChange={(e) => setManualTopupData({ ...manualTopupData, agent_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Agent</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.agency_name} - {agent.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedAgent && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Agent:</p>
                  <p className="font-medium text-gray-800">{selectedAgent.agency_name} - {selectedAgent.full_name}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹) *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={manualTopupData.amount}
                  onChange={(e) => setManualTopupData({ ...manualTopupData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={manualTopupData.notes}
                  onChange={(e) => setManualTopupData({ ...manualTopupData, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Reason for topup..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg"
              >
                Process Topup
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Approve Topup Request</h2>
            
            {/* Request Details */}
            <div className="space-y-3 mb-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Agent</p>
                <p className="font-semibold text-gray-800">{selectedRequest?.agent_name} ({selectedRequest?.agency_name})</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="font-semibold text-gray-800">₹{selectedRequest?.amount?.toLocaleString()}</p>
              </div>
              {selectedRequest?.bank_details && (
                <div>
                  <p className="text-sm text-gray-600">Bank Transfer Details</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedRequest.bank_details}</p>
                </div>
              )}
              {selectedRequest?.notes && (
                <div>
                  <p className="text-sm text-gray-600">Agent Notes</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedRequest.notes}</p>
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes (Optional)</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Add approval notes..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveRequest}
                className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Reject Topup Request</h2>
            <p className="text-gray-600 mb-4">
              Reject ₹{selectedRequest?.amount?.toLocaleString()} topup for {selectedRequest?.agent_name}?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason *</label>
              <textarea
                required
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Reason for rejection..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectRequest}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Proof Modal */}
      {showPaymentProofModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Payment Proof</h2>
              <button onClick={closePaymentProofModal}>
                <X className="text-gray-500 hover:text-gray-700" size={24} />
              </button>
            </div>
            <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[400px]">
              {isPdfUrl(paymentProofUrl) ? (
                <iframe src={paymentProofUrl} className="w-full h-[500px] rounded" title="Payment Proof" />
              ) : (
                <img src={paymentProofUrl} alt="Payment Proof" className="max-w-full max-h-[500px] rounded shadow-lg" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;
